// VX Shop 운영 순수 로직 — tools/export-sheets.gs(VX 싱크)의 로직을 TS로 포팅. I/O 없음.

import type { VerseFlavor } from '@rb/shared/admin-verses';
import type { ProductAvailability } from '@rb/shared/master-data/types/shop.types';

export interface ClientProduct {
  productId: string;
  name: string;
  price: number;
  stock: number;
  imageUrl: string;
  description: string;
  /** 이미 JSON 문자열로 빌드된 metadata (clientProducts CDN 산출물). */
  metadata: string;
  /** object 또는 null (clientProducts 산출물). */
  purchaseConstraints: Record<string, unknown> | null;
  isActive: boolean;
  /** 노출 모드. 구 CDN JSON 호환 위해 optional — 없으면 'both' 로 취급. */
  availability?: ProductAvailability;
}

export interface VxUpsertPayload {
  verseId: string;
  productId: string;
  name: string;
  price: number;
  stock: number;
  imageUrl: string;
  description: string;
  metadata: string;
  purchaseConstraints: Record<string, unknown>;
  isActive: boolean;
}

/** meme-api 목록 응답의 상품 항목(필요한 필드만). */
export interface VxServerItem {
  id: string | number;
  productId: string;
  name?: string;
  description?: string;
  price?: number | string;
  stock?: number | string;
  isActive?: boolean;
  imageUrl?: string | null;
  metadata?: string | null;
  /**
   * meme-api 는 purchaseConstraints 를 별도 컬럼이 아니라 metadata JSON 안의
   * `purchaseConstraints` 키로 병합해 저장/반환한다(응답엔 이 필드가 없음).
   * 구 스키마/타 클라이언트 호환을 위해 optional 로 남겨두고 diff 시 fallback 으로만 쓴다.
   */
  purchaseConstraints?: string | null;
}

export interface FieldDiff {
  field: string;
  old: unknown;
  new: unknown;
}

export interface UpsertPlan {
  creates: { payload: VxUpsertPayload }[];
  updates: { payload: VxUpsertPayload; serverItemId: string | number; diffs: FieldDiff[] }[];
  skips: string[];
  /** 대상 verse flavor 와 안 맞아 서버에서 내릴(isActive=false) 기존 상품. */
  deactivates: { payload: VxUpsertPayload; serverItemId: string | number }[];
}

export function verseOwnerAddress(verseId: string): string {
  const i = verseId.lastIndexOf('-');
  return i > 0 ? verseId.slice(0, i) : '';
}

export function isVerseOwnedBy(verseId: string, account: string | null | undefined): boolean {
  if (!account) return false;
  const owner = verseOwnerAddress(verseId);
  if (!owner) return false;
  return owner.toLowerCase() === account.toLowerCase();
}

export function parseJsonField(v: unknown): Record<string, unknown> {
  if (v == null) return {};
  if (Array.isArray(v)) return {};
  if (typeof v === 'object') return v as Record<string, unknown>;
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return {};
}

export function clientProductToPayload(
  entry: ClientProduct,
  verseId: string,
  testMode: boolean
): VxUpsertPayload {
  return {
    verseId,
    productId: entry.productId,
    name: entry.name,
    price: testMode ? 0 : entry.price,
    stock: entry.stock,
    imageUrl: entry.imageUrl,
    description: entry.description,
    metadata: entry.metadata,
    purchaseConstraints: entry.purchaseConstraints ?? {},
    isActive: entry.isActive,
  };
}

/**
 * 서버가 돌려준 metadata 에서 purchaseConstraints 를 분리한다.
 * meme-api 는 요청의 purchaseConstraints(object)를 metadata JSON 끝의 `purchaseConstraints`
 * 키로 병합해 저장/반환하므로(별도 컬럼 없음), 비교 전에 동일 모델로 정규화해야 한다.
 * - meta: purchaseConstraints 를 제거한 나머지 metadata (CDN clientProducts 와 비교 대상)
 * - constraints: 분리된 purchaseConstraints. metadata 에 없으면 구 스키마의 별도 필드로 fallback.
 */
function splitServerMetadata(server: VxServerItem): {
  meta: Record<string, unknown>;
  constraints: Record<string, unknown>;
} {
  const { purchaseConstraints: embedded, ...meta } = parseJsonField(server.metadata);
  const constraints =
    embedded && typeof embedded === 'object' && !Array.isArray(embedded)
      ? (embedded as Record<string, unknown>)
      : parseJsonField(server.purchaseConstraints);
  return { meta, constraints };
}

export function diffItems(payload: VxUpsertPayload, server: VxServerItem): FieldDiff[] {
  const diffs: FieldDiff[] = [];
  // NOTE: stock 은 의도적으로 제외 — 서버 재고는 판매로 변동되므로 upsert update 트리거로 삼지 않는다.
  // (stock 만 다른 상품은 SKIP. 단 다른 필드 변경으로 update 가 일어나면 payload.stock 은 그대로 전송된다.)
  const scalarFields: (keyof VxUpsertPayload & keyof VxServerItem)[] = [
    'name',
    'description',
    'price',
    'imageUrl',
    'isActive',
  ];
  for (const f of scalarFields) {
    const csv = payload[f] as unknown;
    let srv = server[f] as unknown;
    if (typeof csv === 'number' && typeof srv === 'string') srv = Number(srv);
    if (csv !== srv) diffs.push({ field: f, old: srv, new: csv });
  }

  // meme-api 의 metadata 는 purchaseConstraints 를 병합해 돌려주므로, 서버 metadata 에서
  // 분리해 (a) 순수 metadata 와 (b) purchaseConstraints 를 각각 CDN 값과 비교한다.
  // NOTE: key-order-sensitive compare — CDN(clientProducts)과 서버가 같은 키 순서를 보존한다고 가정
  // (양쪽 같은 빌드 파이프라인; purchaseConstraints 는 항상 끝에 병합되므로 제거 후 순서가 일치).
  const { meta: srvMeta, constraints: srvCon } = splitServerMetadata(server);
  const csvMeta = parseJsonField(payload.metadata);
  if (JSON.stringify(csvMeta) !== JSON.stringify(srvMeta)) {
    diffs.push({ field: 'metadata', old: srvMeta, new: csvMeta });
  }

  const csvCon = payload.purchaseConstraints ?? {};
  if (JSON.stringify(csvCon) !== JSON.stringify(srvCon)) {
    diffs.push({ field: 'purchaseConstraints', old: srvCon, new: csvCon });
  }
  return diffs;
}

export function buildUpsertPlan(
  products: ClientProduct[],
  serverByProductId: Map<string, VxServerItem>,
  verseId: string,
  testMode: boolean,
  targetFlavor?: VerseFlavor
): UpsertPlan {
  const plan: UpsertPlan = { creates: [], updates: [], skips: [], deactivates: [] };
  for (const entry of products) {
    if (!entry.productId) continue;
    const availability = entry.availability ?? 'both';
    const matches = !targetFlavor || availability === 'both' || availability === targetFlavor;
    const server = serverByProductId.get(entry.productId);

    if (!matches) {
      // 대상 verse flavor 와 안 맞음: 서버에 활성 상태로 존재하면 비활성화.
      if (server && server.isActive !== false) {
        const payload = { ...clientProductToPayload(entry, verseId, testMode), isActive: false };
        plan.deactivates.push({ payload, serverItemId: server.id });
      }
      continue;
    }

    const payload = clientProductToPayload(entry, verseId, testMode);
    if (!server) {
      plan.creates.push({ payload });
      continue;
    }
    const diffs = diffItems(payload, server);
    if (diffs.length === 0) {
      plan.skips.push(entry.productId);
    } else {
      plan.updates.push({ payload, serverItemId: server.id, diffs });
    }
  }
  return plan;
}

// ── 판매(sales) 레코드 헬퍼 ──────────────────────────────────────────────

export type SaleRecord = Record<string, unknown>;

export function isRefunded(sale: SaleRecord | null | undefined): boolean {
  if (!sale || typeof sale !== 'object') return false;
  if (sale.refundedAt) return true;
  if (sale.refundDate) return true;
  if (sale.isRefunded === true) return true;
  if (typeof sale.status === 'string' && /refund/i.test(sale.status)) return true;
  if (
    typeof sale.refundStatus === 'string' &&
    sale.refundStatus !== '' &&
    sale.refundStatus.toUpperCase() !== 'NONE'
  )
    return true;
  if (sale.refund && typeof sale.refund === 'object') return true;
  return false;
}

export function extractSales(payload: unknown): SaleRecord[] {
  if (Array.isArray(payload)) return payload as SaleRecord[];
  const p = payload as Record<string, unknown> | null;
  if (p && Array.isArray(p.items)) return p.items as SaleRecord[];
  if (p && Array.isArray(p.data)) return p.data as SaleRecord[];
  if (p && Array.isArray(p.results)) return p.results as SaleRecord[];
  if (p && Array.isArray(p.sales)) return p.sales as SaleRecord[];
  return [];
}
